package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.glok.backend.Texture
import uk.co.nickthecoder.glok.backend.backend
import uk.co.nickthecoder.glok.property.boilerplate.colorProperty
import uk.co.nickthecoder.glok.scene.Color
import uk.co.nickthecoder.glok.util.Matrix
import uk.co.nickthecoder.glok.util.log
import uk.co.nickthecoder.kyd.Actor
import uk.co.nickthecoder.kyd.Resource
import uk.co.nickthecoder.kyd.ShapeResource
import uk.co.nickthecoder.kyd.appearance.Graphic
import uk.co.nickthecoder.kyd.appearance.Pose
import uk.co.nickthecoder.shaders.FlatProgram
import uk.co.nickthecoder.shaders.ShellTexProgram
import uk.co.nickthecoder.shaders.TextureProgram
import uk.co.nickthecoder.shaders.UGradientProgram

interface Paint {

    val name: String

    /**
     * Does each vertex of the [Mesh] require a `u` attribute which governs the color at that vertex?
     * A value of 0 for the inside of the stroke and 1 for the outside.
     *
     * [ShellGradientPaint] is the simplest [Paint] that has a `u` attribute.
     */
    val hasAcrossAttribute: Boolean get() = false

    fun draw(mesh: Mesh, modelMatrix: Matrix)

    /**
     * When a [ShapeResource] is used to create a [ShapeAppearance] for a new [Actor], we need a copy,
     * because the state of a [Paint] are mutable (such as [FlatPaint.color]), and we don't want to share the same
     * instance. i.e. Changing the color of an Actor should NOT change the color of the ShapeResource (or other Actors).
     *
     * @param fromShapeResource The resource we are copying from. This is used to build the absolute-path
     * of any resources used (such as [Textured.graphicOrPosePath]).
     */
    fun copy(fromShapeResource: ShapeResource): Paint
}

object NoPaint : Paint {

    override val name get() = "None"

    override fun draw(mesh: Mesh, modelMatrix: Matrix) {
        // Do nothing
    }

    override fun copy(fromShapeResource: ShapeResource) = this

    override fun toString() = "NoFill"
}

class FlatPaint(

    initialColor: Color

) : Paint, Colored {

    override val colorProperty by colorProperty(initialColor)
    override var color by colorProperty

    override val name get() = NAME

    override fun draw(mesh: Mesh, modelMatrix: Matrix) {
        FlatProgram.draw(mesh, backend.viewMatrix, modelMatrix, color)
    }

    override fun copy(fromShapeResource: ShapeResource) = FlatPaint(color)

    override fun toString() = "FlatFill $color"

    companion object {
        const val NAME = "Flat"
    }
}

class TexturePaint : Paint, Textured, Tinted {


    override val name get() = NAME

    /**
     * This is only required when we are storing this [TexturePaint] in a .kyd or .act file.
     * It is used to populate [texture] once the file has been loaded.
     */
    override var graphicOrPosePath: String = ""

    /**
     * This must be a repeating texture. See [Graphic.repeating] / [Pose.repeating].
     */
    override var texture: Texture? = null

    override val tintProperty by colorProperty(Color.WHITE)
    override var tint by tintProperty

    override fun resolveReferences(relativeTo: Resource): String? {

        val res = relativeTo.find(graphicOrPosePath)
        when (res) {
            is Pose -> {
                texture = res.repeatingTexture()
            }

            is Graphic -> {
                texture = res.texture
            }
        }

        if (texture == null) {
            return "TextureFill couldn't find its texture : '$graphicOrPosePath'"
        }

        return null
    }

    override fun draw(mesh: Mesh, modelMatrix: Matrix) {
        val texture = texture
        if (texture == null) {
            // Fallback when texture is null.
            FlatProgram.draw(mesh, backend.viewMatrix, modelMatrix, Color.DARK_RED)
        } else {
            TextureProgram.draw(mesh, backend.viewMatrix, modelMatrix, texture, tint)
        }
    }

    override fun copy(fromShapeResource: ShapeResource) = TexturePaint().also {
        it.graphicOrPosePath = fromShapeResource.find(graphicOrPosePath)?.path() ?: ""
        it.texture = texture
        it.tint = tint
    }

    override fun toString() = "TextureFill path '$graphicOrPosePath' tint : $tint"

    companion object {
        const val NAME = "Texture"
    }
}


/**
 * A gradient from the inside edge of the stroke to the outside edge of the stroke.
 *
 * The [Mesh] includes `u` values for each vertex. 0 for the inside edge and 1 for the outside edge.
 * [color] is used at 0 and [toColor] at 1. Intermediate values are weighted averages of each of the rgba
 * color channels.
 */
class ShellGradientPaint(

    initialColor: Color,
    initialToColor: Color

) : Paint, WithGradient {

    override val colorProperty by colorProperty(initialColor)
    override var color by colorProperty

    override val toColorProperty by colorProperty(initialToColor)
    override var toColor by toColorProperty


    override val name get() = NAME

    override val hasAcrossAttribute: Boolean get() = true

    override fun draw(mesh: Mesh, modelMatrix: Matrix) {
        if (mesh.floatsPerVertex == 3) {
            UGradientProgram.draw(mesh, backend.viewMatrix, modelMatrix, color, toColor)
        } else {
            FlatProgram.draw(mesh, backend.viewMatrix, modelMatrix, color)
        }
    }

    override fun copy(fromShapeResource: ShapeResource) = ShellGradientPaint(color, toColor)

    override fun toString() = "GradientStroke $color to $toColor"

    companion object {
        const val NAME = "Shell"
    }
}


/**
 *
 */
class ShellTexPaint : Paint, Textured {

    override val name get() = NAME

    override val hasAcrossAttribute: Boolean get() = true

    /**
     * This is only required when we are storing this [TexturePaint] in a .kyd or .act file.
     * It is used to populate [texture] once the file has been loaded.
     */
    override var graphicOrPosePath: String = ""

    /**
     * This must be a repeating texture. See [Graphic.repeating] / [Pose.repeating].
     */
    override var texture: Texture? = null


    override fun resolveReferences(relativeTo: Resource): String? {

        val res = relativeTo.find(graphicOrPosePath)
        when (res) {
            is Pose -> {
                texture = res.repeatingTexture()
            }

            is Graphic -> {
                texture = res.texture
            }
        }

        if (texture == null) {
            return "ShellTexPaint couldn't find its texture : '$graphicOrPosePath'"
        }

        return null
    }

    override fun draw(mesh: Mesh, modelMatrix: Matrix) {
        texture?.let {
            ShellTexProgram.draw(mesh, backend.viewMatrix, modelMatrix, it)
        }
    }

    override fun copy(fromShapeResource: ShapeResource) = ShellTexPaint().also {
        it.graphicOrPosePath = fromShapeResource.find(graphicOrPosePath)?.path() ?: ""
        it.texture = texture
    }

    override fun toString() = "ShellTexPaint $graphicOrPosePath"

    companion object {
        const val NAME = "ShellTex"
    }
}
